import React, { useState } from 'react';
import DayliPromise from '../common/DayliPromise';
import './SignupStep.css';

export interface SignupStepProps {
  onNext: () => void;
}

const SignupStep: React.FC<SignupStepProps> = ({ onNext }) => {
  const [formData, setFormData] = useState({
    legalName: '',
    emailAddress: '',
    phoneNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate Legal Name
    if (!formData.legalName.trim()) {
      newErrors.legalName = 'Legal name is required';
    } else if (formData.legalName.trim().length < 2) {
      newErrors.legalName = 'Legal name must be at least 2 characters';
    }

    // Validate Email
    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = 'Email address is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.emailAddress)) {
        newErrors.emailAddress = 'Please enter a valid email address';
      }
    }

    // Validate Phone Number
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else {
      const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
      if (!phoneRegex.test(formData.phoneNumber.replace(/\s/g, ''))) {
        newErrors.phoneNumber = 'Please enter a valid phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      onNext();
    } catch (error) {
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-step">
      {/* Main Content Container */}
      <div className="signup-content-container">
        {/* Left Side - Form Section */}
        <div className="signup-form-section">
          <h1 className="signup-main-heading">Enter your details to get Dayli card</h1>

          {/* Form Fields */}
          <div className="signup-form-fields">
            <div className="signup-input-field">
              <label className="signup-field-label">Legal Name</label>
              <div className="signup-field-container">
                <input
                  type="text"
                  className="signup-field-input"
                  placeholder="Legal Name"
                  value={formData.legalName}
                  onChange={(e) => handleInputChange('legalName', e.target.value)}
                />
              </div>
              {errors.legalName && <span className="signup-field-error">{errors.legalName}</span>}
            </div>

            <div className="signup-input-field">
              <label className="signup-field-label">Email</label>
              <div className="signup-field-container">
                <input
                  type="email"
                  className="signup-field-input"
                  placeholder="Email"
                  value={formData.emailAddress}
                  onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                />
              </div>
              {errors.emailAddress && <span className="signup-field-error">{errors.emailAddress}</span>}
            </div>

            <div className="signup-input-field">
              <label className="signup-field-label">Phone number</label>
              <div className="signup-field-container">
                <input
                  type="tel"
                  className="signup-field-input"
                  placeholder="Phone Number"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                />
              </div>
              {errors.phoneNumber && <span className="signup-field-error">{errors.phoneNumber}</span>}
            </div>
          </div>

          {/* Terms and Button Section */}
          <div className="signup-terms-and-button">
            <div className="signup-terms-text">
              By clicking "Submit", I agree to the Privacy Policy and Terms of use
              <br /><br />
              To help the government fight terrorism funding and money laundering, all financial institutions are required by federal law to obtain, verify, and record certain information about you, including your name, address, and date of birth. We may also ask to see your driver's license or other identifying documents. We'll let you know if additional information is required.
              <br /><br />
              In order for us to make a decision, you may be required to link a bank account.
            </div>

            <button
              type="button"
              className="signup-continue-button"
              onClick={handleContinue}
              disabled={loading}
            >
              <span className="signup-continue-text">
                {loading ? 'Submitting...' : 'Continue'}
              </span>
            </button>
          </div>
        </div>

        {/* Right Side - Dayli Promise Section */}
        <div className="signup-promise-section">
          <DayliPromise />
        </div>
      </div>
    </div>
  );
};

export default SignupStep; 